/**
 * One player's WebRTC connection to a match's voice room, through our server
 * to Cloudflare's SFU. The negotiation is ported from the spike that proved it
 * against Cloudflare (spikes/voice/cloudflare/cf-page.mjs: publish, pull,
 * closePull, rollback); spikes do not ship, so this is a copy, not an import.
 *
 * ONE RTCPeerConnection per joined match, which is one Cloudflare session. The
 * server makes that session on this connection's FIRST pull or publish, not on
 * `voice/join` - Cloudflare ends a session nothing connects to within 10-15 s,
 * and a listener in a quiet room may have nothing to negotiate for minutes.
 * Every later publish, pull and close renegotiates it.
 *
 * ONE promise queue, which every session mutation goes through as a whole
 * exchange - the API call AND the setRemote/createAnswer/setLocal AND the
 * renegotiate. An RTCPeerConnection has one signaling state; two exchanges
 * interleaved (a pull's offer arriving while a close's offer is outstanding)
 * leave it in a state neither expected, and the failure shows up as silence
 * rather than as an error.
 *
 * Failures are not patched. A refusal BEFORE any description was applied left
 * nothing changed, so the pulls are simply re-synced from the roster a little
 * later. A failure AFTER one was applied means this side and Cloudflare may no
 * longer agree about the session, and the only state both can agree on is a new
 * one: the client reports itself broken and the store rejoins.
 */
import {
  voiceClose,
  voiceConnect,
  voiceErrorCode,
  voicePublish,
  voicePull,
  voiceRenegotiate,
  voiceState,
} from "../net/voiceApi";
import { diffPulls, type PullRecord, type VoiceRoom } from "./rules";
import { withVoiceOpus } from "./sdp";
import { audioFix } from "../audio/audioDiag";
import { attachRemoteTrack, detachRemoteTrack, noteMicCapture, silenceRemote } from "./voiceAudio";

/** The server takes at most this many ids per pull. */
const PULL_BATCH = 16;
/** Cloudflare drops a track that sends nothing for 30 s; re-publish well before that. */
const STALL_MS = 10_000;
const WATCHDOG_MS = 2_000;
const RETRY_MIN_MS = 1_000;
const RETRY_MAX_MS = 30_000;

export const MIC_CONSTRAINTS: MediaStreamConstraints = {
  // Echo cancellation is what stops the table's own sound effects being sent
  // to everyone at it.
  audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
};

/**
 * EXPERIMENT audiofix=noaec: capture with no voice processing. On an iPhone,
 * echo cancellation puts audio through iOS's call-mode processing, OUTPUT
 * included - the suspect for the game's music crackling while the mic is held.
 * Without it the table's sound (and other voices on a speaker) leak into the mic.
 */
function micConstraints(): MediaStreamConstraints {
  return audioFix("noaec") ? { audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } } : MIC_CONSTRAINTS;
}


class ClosedError extends Error {
  constructor() {
    super("voice connection closed");
  }
}

/** Something went wrong after the session had already been touched. */
class BrokenSessionError extends Error {}

/**
 * Wait for ICE gathering, bounded: the offer goes to the server whole (no
 * trickle), but a page must not hang on a slow STUN reply. The host candidates
 * are there long before the cap, and Cloudflare connects on those.
 */
function gathered(pc: RTCPeerConnection, ms = 2000): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener("icegatheringstatechange", onChange);
      clearTimeout(timer);
      resolve();
    };
    const onChange = () => {
      if (pc.iceGatheringState === "complete") done();
    };
    const timer = setTimeout(done, ms);
    pc.addEventListener("icegatheringstatechange", onChange);
  });
}

/** An SDP cut to its structure - sections, ports, mids, directions, BUNDLE - for a DEV log line. */
function sdpShape(sdp: string): string {
  return sdp
    .split(/\r?\n/)
    .filter((l) => l.startsWith("m=") || l.startsWith("a=group:") || /^a=(sendonly|recvonly|sendrecv|inactive|mid:|bundle-only)/.test(l))
    .map((l) => (l.startsWith("m=") ? l.split(" ").slice(0, 2).join(" ") : l))
    .join(" ");
}

function log(...args: unknown[]): void {
  if (import.meta.env.DEV) console.debug("[voice]", ...args);
}

export interface VoiceClientOptions {
  roomId: string;
  iceServers: RTCIceServer[];
  /** The roster and who to pull, read fresh at the moment a sync runs - never a copy from when it was queued. */
  plan: () => { room: VoiceRoom; desired: string[] };
  /** Pulls or the publish changed. */
  onChange: () => void;
  /** The session can no longer be trusted; the owner should leave and join again. */
  onBroken: (reason: string) => void;
  /**
   * Nothing is left on this connection to negotiate - the last pull was just
   * closed and there is no mic. Not a failure: the owner swaps in a fresh
   * session (see closeMids for why this cannot be negotiated).
   */
  onEmptied: () => void;
}

export class VoiceClient {
  readonly roomId: string;
  private readonly pc: RTCPeerConnection;
  private readonly opts: VoiceClientOptions;
  private queue: Promise<void> = Promise.resolve();
  private closed = false;

  private readonly pulls = new Map<string, PullRecord>();
  private readonly midToPlayer = new Map<string, string>();
  /** Tracks that arrived (ontrack) before the pull that named them was recorded. */
  private readonly unclaimedTracks = new Map<string, MediaStreamTrack>();
  /** Pulled tracks that ended on their own; closed on the next sync, then re-pulled. */
  private readonly endedMids = new Set<string>();
  /** Mids this side is closing, whose `ended` is expected. */
  private readonly closingMids = new Set<string>();
  /** The server said not-pullable at this trackGen; not asked again until it moves. */
  private readonly refused = new Map<string, number>();

  private mic: { track: MediaStreamTrack; transceiver: RTCRtpTransceiver; mid: string } | null = null;
  private micEnabled = false;
  private republishing = false;

  private syncQueued = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelay = RETRY_MIN_MS;
  private watchdog: ReturnType<typeof setInterval> | null = null;
  private lastPackets = -1;
  private lastMoveAt = 0;

  constructor(opts: VoiceClientOptions) {
    this.opts = opts;
    this.roomId = opts.roomId;
    this.pc = new RTCPeerConnection({ iceServers: opts.iceServers, bundlePolicy: "max-bundle" });
    this.pc.addEventListener("track", (e) => this.onTrack(e));
    this.pc.addEventListener("connectionstatechange", () => {
      log("connection", this.pc.connectionState);
      // "disconnected" recovers on its own; "failed" never does.
      if (this.pc.connectionState === "failed") this.broken("connection failed");
    });
  }

  get published(): boolean {
    return this.mic !== null;
  }

  /** The published mic track, for measuring how loud it is; null while muted (muting unpublishes). */
  get micTrack(): MediaStreamTrack | null {
    return this.mic?.track ?? null;
  }

  get pulledPlayerIds(): string[] {
    return [...this.pulls.keys()];
  }

  /** Runs `task` after everything already queued; a closed client refuses it. */
  private enqueue<T>(task: () => Promise<T>): Promise<T> {
    const run = this.queue.then(() => {
      if (this.closed) throw new ClosedError();
      return task();
    });
    this.queue = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  private broken(reason: string): void {
    if (this.closed) return;
    log("broken:", reason);
    this.opts.onBroken(reason);
  }

  // ---- the anchor ----------------------------------------------------------

  /**
   * The connection's first exchange, queued ahead of everything else: one
   * trackless send-only audio section, from which the server creates the Cloudflare
   * session. That section is the ANCHOR and is never closed.
   *
   * Under max-bundle the first section is the one the shared transport is tagged
   * to, and closing it kills the whole Cloudflare session (measured 2026-10-09:
   * the next call answers 410). Without an anchor the first section was the mic
   * of whoever unmuted first, or the first pull - exactly what muting (which
   * unpublishes) or a speaker leaving closes. With it, every mic and pull can be
   * closed, and closing the last of them is still a negotiable offer.
   */
  connect(): void {
    this.enqueue(async () => {
      // Sendonly with no track: Cloudflare treats a recvonly section as one it
      // may send on and fills it with the first pull (ValidateAnchorOffer).
      this.pc.addTransceiver("audio", { direction: "sendonly" });
      let touched = false;
      try {
        await this.pc.setLocalDescription(await this.pc.createOffer());
        touched = true;
        await gathered(this.pc);
        const answer = await voiceConnect(this.roomId, this.pc.localDescription!.sdp);
        if (!answer) throw new Error("connect returned no description");
        await this.pc.setRemoteDescription(answer);
        log(
          "anchor answered:",
          answer.sdp
            .split(/\r?\n/)
            .filter((l) => l.startsWith("m=") || l.startsWith("a=group:") || /^a=(sendonly|recvonly|sendrecv|inactive|mid:)/.test(l))
            .join(" | "),
        );
      } catch (err) {
        if (touched) await this.rollback();
        throw err;
      }
    }).catch((err: unknown) => {
      // Nothing works without it: a fresh join is the only way to try again.
      if (!(err instanceof ClosedError)) this.broken(`connect failed: ${String((err as Error)?.message ?? err)}`);
    });
  }

  // ---- the mic -------------------------------------------------------------

  /**
   * Opens the mic and publishes it.
   *
   * getUserMedia is started SYNCHRONOUSLY, from inside the tap that asked for
   * it - that tap is the moment the player chose to talk, so it is the moment
   * the permission prompt belongs to - and it is outside the queue, because the
   * prompt can sit there as long as somebody takes to read it and nobody's
   * pulls should wait behind that.
   */
  publish(): Promise<void> {
    // No mediaDevices at all on an insecure origin (a LAN address over http).
    // A rejection, not a throw, so the caller's busy state always comes down.
    if (!navigator.mediaDevices?.getUserMedia) {
      return Promise.reject(new DOMException("this page cannot use a microphone", "NotSupportedError"));
    }
    const media = navigator.mediaDevices.getUserMedia(micConstraints());
    return media.then((stream) => {
      const track = stream.getAudioTracks()[0];
      if (!track) throw new Error("no microphone");
      if (this.closed) {
        track.stop();
        throw new ClosedError();
      }
      track.enabled = this.micEnabled;
      return this.enqueue(() => this.publishTrack(track));
    });
  }

  /**
   * Instant silence on the track that is published, without unpublishing it:
   * for the moments a mute has to hold before anything can be negotiated (a
   * host mute the server is about to force-close, a failed publish). A player's
   * own mute is `mute()`.
   */
  setMicEnabled(on: boolean): void {
    this.micEnabled = on;
    if (this.mic) this.mic.track.enabled = on;
  }

  /**
   * The player muted: UNPUBLISH. The captured track is stopped on the tap - the
   * mic is released, and nothing more is sent - and the mic's section is then
   * closed like any pull, in the queue, which also tells the server this player
   * is no longer published. The next unmute is an ordinary first publish.
   *
   * Muting used to be `enabled = false` on a track that stayed published, which
   * made unmuting instant but kept the mic captured for the rest of the match.
   * On an iPhone a captured mic holds iOS in call mode, whose output processing
   * made the game's music crackle continuously (2026-10-09), and every platform
   * showed the mic as in use to a player who had muted it. Sending generated
   * silence in its place to keep the track alive was tried and broke on the
   * iPhone: with echo cancellation on it stopped sending altogether (Cloudflare
   * answered every pull of it "track not found", dev logs 2026-10-09) - most
   * likely because leaving call mode suspends the audio context making the
   * silence, though that part is inferred. So muting costs a publish on the
   * next unmute (1-3 s before others hear), and holds nothing open meanwhile.
   */
  mute(): void {
    this.micEnabled = false;
    const mic = this.mic;
    if (!mic) return;
    // Not published from here on, so an unmute tapped before the close below has
    // run takes the publish path, which queues behind it.
    this.mic = null;
    this.stopWatchdog();
    mic.track.stop();
    noteMicCapture(null);
    this.opts.onChange();
    this.enqueue(() => this.closeMids([mic.mid])).catch((err: unknown) => {
      if (err instanceof ClosedError || this.closed) return;
      this.broken(`unpublish failed: ${String((err as Error)?.message ?? err)}`);
    });
  }

  /** Tells the room whether this mic is off, in order after any publish ahead of it. */
  sendMicState(selfMuted: boolean): Promise<void> {
    return this.enqueue(() => voiceState(this.roomId, selfMuted));
  }

  private async publishTrack(track: MediaStreamTrack): Promise<void> {
    const previous = this.mic;
    if (previous) {
      // A re-publish: the old transceiver goes, and the server mints the next
      // trackName for the new one, which bumps trackGen and makes every
      // listener drop the dead track and pull the new.
      this.mic = null;
      this.stopWatchdog();
      try {
        previous.transceiver.stop();
      } catch {
        /* already stopped */
      }
      if (previous.track !== track) previous.track.stop();
    }
    noteMicCapture(track);
    const transceiver = this.pc.addTransceiver(track, { direction: "sendonly" });
    let touched = false;
    try {
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      touched = true;
      await gathered(this.pc);
      const mid = transceiver.mid;
      if (!mid || !this.pc.localDescription) throw new Error("publish offer has no mid");
      const answer = await voicePublish(this.roomId, this.pc.localDescription.sdp, mid);
      if (!answer) throw new Error("publish returned no description");
      // DTX, mono and the bitrate cap go on the ANSWER: a sender takes its
      // Opus parameters from the remote description, which here is Cloudflare's.
      await this.pc.setRemoteDescription({ type: answer.type, sdp: withVoiceOpus(answer.sdp) });
      // Not expected for a publish, but if Cloudflare ever counter-offers, answer it.
      if (answer.type === "offer") await this.answerAndRenegotiate();
      this.mic = { track, transceiver, mid };
      track.addEventListener("ended", () => this.republish("mic track ended"));
      this.startWatchdog();
      this.opts.onChange();
    } catch (err) {
      await this.rollback();
      try {
        transceiver.stop();
      } catch {
        /* never started */
      }
      track.stop();
      noteMicCapture(null);
      this.opts.onChange();
      // The transceiver this offer added is now a stopped section in every
      // later offer, and Cloudflare may or may not have taken the track; a new
      // session is the only state both sides agree on.
      if (touched || previous) this.broken(`publish failed: ${String((err as Error)?.message ?? err)}`);
      throw err;
    }
  }

  /**
   * Re-publish after the sender went quiet or the mic track ended. Reuses the
   * track when it is still live; asks for the mic again when it is not (an iOS
   * PWA loses it in the background), which with the permission already given
   * does not prompt.
   */
  private republish(reason: string): void {
    if (this.closed || this.republishing || !this.mic) return;
    log("re-publishing:", reason);
    this.republishing = true;
    const old = this.mic.track;
    const next: Promise<MediaStreamTrack> =
      old.readyState === "live"
        ? Promise.resolve(old)
        : navigator.mediaDevices.getUserMedia(micConstraints()).then((s) => {
            const t = s.getAudioTracks()[0];
            if (!t) throw new Error("no microphone");
            return t;
          });
    next
      .then((track) => {
        track.enabled = this.micEnabled;
        // Muted (unpublished) while the new track was being got: nothing to re-publish.
        return this.enqueue(() => {
          if (this.mic) return this.publishTrack(track);
          if (track !== old) track.stop();
          return Promise.resolve();
        });
      })
      .catch((err) => {
        if (!(err instanceof ClosedError)) this.broken(`re-publish failed: ${String((err as Error)?.message ?? err)}`);
      })
      .finally(() => {
        this.republishing = false;
      });
  }

  /**
   * Cloudflare garbage-collects a track that sends nothing for 30 seconds, and
   * does it silently: listeners hear nothing and the roster still says
   * published. A published mic is an unmuted one (muting unpublishes), and even
   * a quiet room sends DTX, so a sender whose packet count stops moving for 10 s
   * while connected is a backgrounded or suspended page,
   * and the fix is a fresh track. Hidden time does not count: a hidden page is
   * allowed to stall, and gets a full 10 s after it comes back.
   */
  private startWatchdog(): void {
    this.stopWatchdog();
    this.lastPackets = -1;
    this.lastMoveAt = Date.now();
    this.watchdog = setInterval(() => void this.checkSender(), WATCHDOG_MS);
  }

  private stopWatchdog(): void {
    if (this.watchdog !== null) clearInterval(this.watchdog);
    this.watchdog = null;
  }

  private async checkSender(): Promise<void> {
    const mic = this.mic;
    if (!mic || this.closed || this.republishing) return;
    const now = Date.now();
    if (document.visibilityState === "hidden" || this.pc.connectionState !== "connected") {
      this.lastMoveAt = now;
      return;
    }
    let packets = -1;
    try {
      const report = await mic.transceiver.sender.getStats();
      report.forEach((r: { type?: string; packetsSent?: number }) => {
        if (r.type === "outbound-rtp") packets = Math.max(packets, r.packetsSent ?? 0);
      });
    } catch {
      return;
    }
    if (packets !== this.lastPackets) {
      this.lastPackets = packets;
      this.lastMoveAt = now;
      return;
    }
    if (now - this.lastMoveAt >= STALL_MS) this.republish("sender stalled");
  }

  // ---- pulls ---------------------------------------------------------------

  /**
   * Brings the pulls in line with the roster. Coalesced: however many roster
   * snapshots arrive while an exchange is running, one sync runs after it, and
   * it reads the roster as it is THEN.
   */
  sync(): void {
    if (this.closed || this.syncQueued) return;
    this.syncQueued = true;
    this.enqueue(async () => {
      this.syncQueued = false;
      await this.runSync();
    }).catch((err) => this.syncFailed(err));
  }

  private syncFailed(err: unknown): void {
    if (err instanceof ClosedError || this.closed) return;
    if (err instanceof BrokenSessionError) {
      this.broken(err.message);
      return;
    }
    // Nothing was applied, so nothing needs undoing: try the roster again
    // shortly, backing off so a server that is refusing is not hammered.
    log("sync failed, retrying in", this.retryDelay, "ms:", (err as Error)?.message ?? err);
    this.retryLater();
  }

  private retryLater(): void {
    if (this.closed || this.retryTimer !== null) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.sync();
    }, this.retryDelay);
    this.retryDelay = Math.min(RETRY_MAX_MS, this.retryDelay * 2);
  }

  private async runSync(): Promise<void> {
    const { room, desired } = this.opts.plan();
    // A refusal stands until the speaker's track changes.
    for (const [id, gen] of this.refused) if (room.members[id]?.trackGen !== gen) this.refused.delete(id);
    const wanted = desired.filter((id) => !this.refused.has(id));
    const { toPull, toClose } = diffPulls(this.pulls, room, wanted);
    const closing = [...new Set([...toClose, ...this.endedMids])];
    if (closing.length) await this.closeMids(closing);
    let failed = 0;
    for (let i = 0; i < toPull.length; i += PULL_BATCH) {
      failed += await this.pullPlayers(toPull.slice(i, i + PULL_BATCH), room);
    }
    // A pull Cloudflare would not hand over is asked for again, backing off:
    // nothing else would ask. The usual cause is a speaker who has only just
    // published - the roster says so as soon as Cloudflare answers their offer,
    // which is before their connection is up and sending, and Cloudflare will
    // not hand out a track it has had no packets for. The roster does not
    // change again when the packets start, so without this the listener stays
    // deaf to them until something unrelated rewrites it.
    if (failed > 0) {
      log(failed, "pull(s) refused, retrying in", this.retryDelay, "ms");
      this.retryLater();
    } else this.retryDelay = RETRY_MIN_MS;
  }

  /** Resolves to how many of `playerIds` Cloudflare refused, which are worth asking for again. */
  private async pullPlayers(playerIds: string[], room: VoiceRoom): Promise<number> {
    const res = await voicePull(this.roomId, playerIds).catch((err: unknown) => {
      // Nothing was applied, but there is no session left to apply anything
      // to: retrying would only be told the same thing.
      if (voiceErrorCode(err) === "session-gone") throw new BrokenSessionError("pull: session gone");
      throw err;
    });
    const claimed: string[] = [];
    let failed = 0;
    for (const t of res.tracks) {
      if (t.error || !t.mid) {
        if (t.error === "not-pullable") this.refused.set(t.playerId, room.members[t.playerId]?.trackGen ?? -1);
        else failed++;
        continue;
      }
      // Recorded BEFORE the description is applied, because ontrack fires
      // during setRemoteDescription and has to know whose track it is holding.
      this.pulls.set(t.playerId, { trackGen: t.trackGen, mid: t.mid });
      this.midToPlayer.set(t.mid, t.playerId);
      claimed.push(t.mid);
    }
    const desc = res.sessionDescription;
    if (desc) log(`pull ${desc.type}:`, sdpShape(desc.sdp));
    try {
      if (desc?.type === "offer") {
        await this.pc.setRemoteDescription(desc);
        await this.answerAndRenegotiate();
      } else if (desc?.type === "answer") {
        await this.pc.setRemoteDescription(desc);
      } else if (claimed.length > 0) {
        throw new Error("pull returned tracks but no description");
      }
    } catch (err) {
      for (const mid of claimed) this.forgetMid(mid);
      await this.rollback();
      throw new BrokenSessionError(`pull failed: ${String((err as Error)?.message ?? err)}`);
    }
    for (const mid of claimed) {
      const track = this.unclaimedTracks.get(mid);
      const playerId = this.midToPlayer.get(mid);
      if (track && playerId) {
        this.unclaimedTracks.delete(mid);
        attachRemoteTrack(playerId, track);
      }
    }
    if (claimed.length) this.opts.onChange();
    return failed;
  }

  private async closeMids(mids: string[]): Promise<void> {
    let anyLocal = false;
    for (const mid of mids) {
      const playerId = this.midToPlayer.get(mid);
      if (playerId) silenceRemote(playerId);
      const t = this.pc.getTransceivers().find((x) => x.mid === mid);
      if (t && t.currentDirection !== "stopped") {
        anyLocal = true;
        this.closingMids.add(mid);
        try {
          t.stop();
        } catch {
          /* already stopped */
        }
      }
      this.forgetMid(mid);
      this.endedMids.delete(mid);
    }
    this.opts.onChange();
    // Nothing of these exists on this side: there is no offer to make, and
    // the stopped-transceiver offer below would be an offer about nothing.
    if (!anyLocal) return;
    // Closing the LAST live transceiver cannot be negotiated at all: with
    // max-bundle, an offer whose every section is stopped has no BUNDLE group
    // and setLocalDescription throws (spike 1.2, test 6 - a listener with no
    // mic muting the only person talking). The transceivers are stopped, so the
    // mute already holds locally; what is left is a Cloudflare session still
    // forwarding to nobody, and a fresh session is the clean way out of it.
    if (!this.pc.getTransceivers().some((t) => t.direction !== "stopped")) {
      this.opts.onEmptied();
      return;
    }
    try {
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      await gathered(this.pc);
      log("close offer:", sdpShape(this.pc.localDescription!.sdp));
      const res = await voiceClose(this.roomId, mids, this.pc.localDescription!.sdp);
      const desc = res.sessionDescription;
      if (desc) log(`close ${desc.type}:`, sdpShape(desc.sdp));
      if (desc?.type === "answer") {
        await this.pc.setRemoteDescription(desc);
      } else if (desc?.type === "offer") {
        await this.pc.setRemoteDescription(desc);
        await this.answerAndRenegotiate();
      } else {
        // No description came back: roll the dangling offer back so the
        // connection is usable for the next exchange.
        await this.rollback();
      }
    } catch (err) {
      await this.rollback();
      // The transceivers are stopped here whatever Cloudflare thinks, so the
      // mute holds; it is the session that can no longer be trusted.
      throw new BrokenSessionError(`close failed: ${String((err as Error)?.message ?? err)}`);
    }
  }

  private forgetMid(mid: string): void {
    const playerId = this.midToPlayer.get(mid);
    this.midToPlayer.delete(mid);
    this.unclaimedTracks.delete(mid);
    if (playerId && this.pulls.get(playerId)?.mid === mid) {
      this.pulls.delete(playerId);
      detachRemoteTrack(playerId);
    }
  }

  private onTrack(e: RTCTrackEvent): void {
    const mid = e.transceiver?.mid;
    if (!mid || e.track.kind !== "audio") return;
    const track = e.track;
    track.addEventListener("ended", () => this.onRemoteEnded(mid));
    const playerId = this.midToPlayer.get(mid);
    if (playerId) attachRemoteTrack(playerId, track);
    else this.unclaimedTracks.set(mid, track);
  }

  /** A pulled track that ends without us closing it is re-pulled from the roster. */
  private onRemoteEnded(mid: string): void {
    if (this.closed) return;
    if (this.closingMids.delete(mid)) return;
    if (!this.midToPlayer.has(mid)) return;
    log("pulled track ended, re-pulling:", mid);
    this.endedMids.add(mid);
    this.sync();
  }

  // ---- shared negotiation --------------------------------------------------

  private async answerAndRenegotiate(): Promise<void> {
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    await gathered(this.pc);
    await voiceRenegotiate(this.roomId, this.pc.localDescription!.sdp);
  }

  /**
   * Back to `stable` after an exchange that failed part-way. A connection left
   * in have-local-offer or have-remote-offer throws on the next exchange's
   * first description, which would turn one failure into every later one.
   */
  private async rollback(): Promise<void> {
    const state = this.pc.signalingState;
    try {
      if (state === "have-local-offer") await this.pc.setLocalDescription({ type: "rollback" });
      else if (state === "have-remote-offer") await this.pc.setRemoteDescription({ type: "rollback" });
    } catch {
      /* closed underneath us; nothing left to roll back */
    }
  }

  /** Hangs up locally. Leaving the room on the server is the owner's call. */
  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.stopWatchdog();
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    if (this.mic) {
      this.mic.track.stop();
      noteMicCapture(null);
    }
    this.mic = null;
    for (const playerId of this.pulls.keys()) detachRemoteTrack(playerId);
    this.pulls.clear();
    this.midToPlayer.clear();
    this.unclaimedTracks.clear();
    try {
      this.pc.close();
    } catch {
      /* already closed */
    }
  }
}
